import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V13 — "Cyber-Heritage"
   Collision of traditional motifs with digital glitches. Neon-lit borders,
   neo-serif typography (traditional shapes with futuristic sharp edges).
   Cinzel (classical heritage) + Rajdhani (cyberpunk body). */

const dark = '#0c0c0e';
const cream = '#f0e8d8';
const neonMagenta = '#ff2d78';
const neonCyan = '#00f0ff';
const gold = '#c9a94e';
const dimText = '#706858';

const features = [
  { glyph: 'Ⅰ', title: 'Agenda intelligent', desc: 'Synchronisation des créneaux projetée dans un flux temporel continu.' },
  { glyph: 'Ⅱ', title: 'Dossiers patients', desc: 'Archives vivantes, augmentées de métadonnées cliniques en couche.' },
  { glyph: 'Ⅲ', title: 'Facturation fluide', desc: 'Transactions inscrites dans un registre numérique infalsifiable.' },
  { glyph: 'Ⅳ', title: 'Multi-cabinet', desc: 'Réseau maillé de sites unifié sous un protocole unique.' },
];

export default function LandingV13() {
  const heritage = "'Cinzel', serif";
  const cyber = "'Rajdhani', sans-serif";

  return (
    <div className="doctif-page" style={{ fontFamily: cyber, background: dark, color: cream }}>
      <Helmet><title>doctif — Cyber-Heritage</title></Helmet>

      {/* Digital noise overlay */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0.03,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat', backgroundSize: '200px',
      }} />

      {/* Scanline effect */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,240,255,0.015) 2px, rgba(0,240,255,0.015) 4px)`,
      }} />

      {/* Neon top line */}
      <div style={{
        height: 2,
        background: `linear-gradient(90deg, ${neonMagenta}, ${neonCyan}, ${neonMagenta})`,
        boxShadow: `0 0 20px ${neonMagenta}44, 0 0 40px ${neonCyan}22`,
      }} />

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '28px 0 20px', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{
            fontFamily: heritage, fontSize: 30, fontWeight: 700, letterSpacing: '0.15em',
            textTransform: 'uppercase',
            background: `linear-gradient(135deg, ${cream}, ${gold})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            Doctif
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <nav className="doctif-chip-row">
              {Array.from({ length: 15 }, (_, i) => (
                <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                  borderRadius: 0, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                  fontFamily: cyber,
                  background: i === 12 ? neonMagenta : 'transparent',
                  color: i === 12 ? dark : dimText,
                  border: `1px solid ${i === 12 ? neonMagenta : '#333'}`,
                  boxShadow: i === 12 ? `0 0 12px ${neonMagenta}44` : 'none',
                }}>{String(i + 1).padStart(2, '0')}</a>
              ))}
            </nav>
            <a href="/accounts/login/" style={{
              fontFamily: heritage, fontWeight: 400, fontSize: 12, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: neonCyan, textDecoration: 'none',
              padding: '7px 18px', border: `1px solid ${neonCyan}44`,
              boxShadow: `0 0 10px ${neonCyan}22`, transition: 'all 0.3s',
            }}>Accès</a>
          </div>
        </div>
        {/* Decorative border — classical with neon */}
        <div style={{
          height: 1, marginTop: 16,
          background: `linear-gradient(90deg, transparent, ${gold}44, ${neonCyan}44, ${gold}44, transparent)`,
        }} />
      </header>

      {/* ── Hero ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 60, paddingBottom: 80 }}>
        <p className="doctif-kicker doctif-fade-up" style={{
          color: neonCyan, marginBottom: 24, letterSpacing: '0.35em',
          textShadow: `0 0 20px ${neonCyan}33`,
        }}>
          Héritage ∴ Futur
        </p>
        <h1 className="doctif-fade-up doctif-delay-1" style={{
          fontFamily: heritage, fontWeight: 700,
          fontSize: 'clamp(2.4rem, 6vw, 5.4rem)', lineHeight: 1,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          marginBottom: 28,
        }}>
          <span style={{ color: cream }}>Tradition</span><br />
          <span style={{
            background: `linear-gradient(90deg, ${neonMagenta}, ${neonCyan})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textShadow: 'none',
          }}>Augmentée</span>
        </h1>
        <p className="doctif-fade-up doctif-delay-2" style={{
          fontSize: 16, lineHeight: 1.8, color: dimText, maxWidth: 480, marginBottom: 36,
          fontWeight: 500,
        }}>
          Là où l'héritage clinique rencontre l'infrastructure numérique. doctif fusionne la noblesse
          du métier avec la puissance du code.
        </p>
        <div className="doctif-fade-up doctif-delay-3" style={{ display: 'flex', gap: 14 }}>
          <a href="/accounts/login/" style={{
            display: 'inline-block', padding: '13px 32px',
            background: neonMagenta, color: dark, fontFamily: heritage,
            fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
            textDecoration: 'none', boxShadow: `0 0 24px ${neonMagenta}44`,
            transition: 'all 0.3s',
          }}>Initier</a>
          <a href="#modules" style={{
            display: 'inline-block', padding: '13px 32px',
            border: `1px solid ${gold}44`, color: gold,
            fontFamily: heritage, fontWeight: 400, fontSize: 13, letterSpacing: '0.1em',
            textTransform: 'uppercase', textDecoration: 'none',
            transition: 'all 0.3s',
          }}>Explorer</a>
        </div>
      </section>

      {/* ── Stats — Neon-bordered bar ── */}
      <div style={{
        borderTop: `1px solid ${neonCyan}22`, borderBottom: `1px solid ${neonMagenta}22`,
        position: 'relative', zIndex: 5, padding: '24px 0',
      }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center' }}>
          {[
            { v: 'CCXL+', l: 'Cabinets' },
            { v: 'LXI·K', l: 'RDV / mois' },
            { v: 'XXXIV%', l: 'Temps économisé' },
          ].map((s, i) => (
            <div key={i} style={{ borderRight: i < 2 ? `1px solid #222` : 'none' }}>
              <div style={{ fontFamily: heritage, fontSize: 28, color: cream, letterSpacing: '0.08em' }}>{s.v}</div>
              <div style={{ fontFamily: cyber, fontSize: 11, color: dimText, marginTop: 4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features — Heritage cards with neon accents ── */}
      <section id="modules" className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 72, paddingBottom: 72 }}>
        <p className="doctif-kicker" style={{ color: gold, letterSpacing: '0.3em', marginBottom: 12 }}>Modules</p>
        <h2 style={{
          fontFamily: heritage, fontWeight: 700, fontSize: 'clamp(1.5rem, 2.8vw, 2.4rem)',
          letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 48,
        }}>
          Quatre Piliers
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} className="doctif-fade-up" style={{
              padding: '32px 28px', position: 'relative',
              background: '#131316', border: '1px solid #222',
              animationDelay: `${i * 120}ms`,
              overflow: 'hidden',
            }}>
              {/* Neon corner accents */}
              <div aria-hidden style={{
                position: 'absolute', top: 0, left: 0, width: 30, height: 30,
                borderTop: `2px solid ${i % 2 === 0 ? neonMagenta : neonCyan}`,
                borderLeft: `2px solid ${i % 2 === 0 ? neonMagenta : neonCyan}`,
              }} />
              <div aria-hidden style={{
                position: 'absolute', bottom: 0, right: 0, width: 30, height: 30,
                borderBottom: `2px solid ${i % 2 === 0 ? neonCyan : neonMagenta}`,
                borderRight: `2px solid ${i % 2 === 0 ? neonCyan : neonMagenta}`,
              }} />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 14 }}>
                <span style={{ fontFamily: heritage, fontSize: 32, color: gold, opacity: 0.5, letterSpacing: '0.05em' }}>{f.glyph}</span>
                <h3 style={{ fontFamily: heritage, fontWeight: 700, fontSize: 18, letterSpacing: '0.06em', textTransform: 'uppercase', color: cream }}>{f.title}</h3>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: dimText, fontWeight: 500 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Dashboard Preview — Glitched classical panel ── */}
      <section style={{ position: 'relative', zIndex: 5, paddingBottom: 72 }}>
        <div className="doctif-shell">
          <div style={{
            background: '#0f0f12', border: '1px solid #222', padding: 28,
            position: 'relative', overflow: 'hidden',
          }}>
            {/* Classical pattern top border */}
            <div style={{
              height: 3,
              background: `repeating-linear-gradient(90deg, ${gold}44 0px, ${gold}44 20px, transparent 20px, transparent 30px, ${neonCyan}44 30px, ${neonCyan}44 50px, transparent 50px, transparent 60px)`,
              marginBottom: 24,
            }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontFamily: heritage, fontSize: 16, letterSpacing: '0.1em', textTransform: 'uppercase', color: cream }}>Registre</span>
              <span style={{ fontFamily: cyber, fontSize: 11, color: neonCyan, letterSpacing: '0.08em' }}>LIVE::STREAM</span>
            </div>
            {/* Entries */}
            {[
              { t: '09:00', p: 'Dr. Martin — Contrôle', s: 'CONFIRMÉ', c: neonCyan },
              { t: '09:45', p: 'Dr. Leroy — Détartrage', s: 'EN ATTENTE', c: gold },
              { t: '10:30', p: 'Dr. Fabre — Implant', s: 'CONFIRMÉ', c: neonCyan },
            ].map((e, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '60px 1fr auto', gap: 20,
                padding: '14px 0', borderTop: '1px solid #1a1a1d',
                alignItems: 'center',
              }}>
                <span style={{ fontFamily: cyber, fontSize: 13, color: dimText, fontWeight: 600 }}>{e.t}</span>
                <span style={{ fontFamily: cyber, fontSize: 14, color: '#aaa', fontWeight: 500 }}>{e.p}</span>
                <span style={{
                  fontFamily: heritage, fontSize: 10, letterSpacing: '0.12em',
                  color: e.c, border: `1px solid ${e.c}33`, padding: '3px 10px',
                  boxShadow: `0 0 8px ${e.c}22`,
                }}>{e.s}</span>
              </div>
            ))}
            {/* Bottom classical pattern */}
            <div style={{
              height: 3, marginTop: 20,
              background: `repeating-linear-gradient(90deg, ${neonMagenta}44 0px, ${neonMagenta}44 20px, transparent 20px, transparent 30px, ${gold}44 30px, ${gold}44 50px, transparent 50px, transparent 60px)`,
            }} />
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingBottom: 72 }}>
        <p className="doctif-kicker" style={{ color: gold, letterSpacing: '0.3em', marginBottom: 32 }}>Voix</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {[
            { q: 'Le mélange d\'autorité classique et d\'efficacité digitale est unique.', a: 'Dr. Elise Moreau', r: 'Paris' },
            { q: 'On dirait un manuscrit ancien connecté à un réseau neural.', a: 'Dr. Julien Fabre', r: 'Lyon' },
            { q: 'L\'avenir et la tradition ne se sont jamais si bien entendus.', a: 'Dr. Nadia Hamdi', r: 'Marseille' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '24px 20px', background: '#131316', border: '1px solid #222',
              position: 'relative',
            }}>
              <div aria-hidden style={{
                position: 'absolute', top: 0, right: 0, width: 20, height: 20,
                borderTop: `2px solid ${neonMagenta}`, borderRight: `2px solid ${neonMagenta}`,
              }} />
              <p style={{ fontFamily: heritage, fontStyle: 'italic', fontSize: 15, lineHeight: 1.7, color: '#aaa', marginBottom: 16 }}>
                « {t.q} »
              </p>
              <p style={{ fontFamily: heritage, fontSize: 13, color: cream, letterSpacing: '0.05em' }}>{t.a}</p>
              <p style={{ fontFamily: cyber, fontSize: 11, color: dimText }}>{t.r}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust — Neon grid ── */}
      <div style={{ borderTop: '1px solid #1a1a1d', borderBottom: '1px solid #1a1a1d', position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', padding: '20px 0', textAlign: 'center' }}>
          {[{ k: 'Chiffrement', v: 'AES-256' }, { k: 'Hébergement', v: 'HDS France' }, { k: 'Uptime', v: '99.9%' }, { k: 'Support', v: '< 2h' }].map((t, i) => (
            <div key={i} style={{ borderRight: i < 3 ? '1px solid #1a1a1d' : 'none' }}>
              <div style={{ fontFamily: cyber, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: dimText, marginBottom: 4 }}>{t.k}</div>
              <div style={{ fontFamily: heritage, fontSize: 16, color: cream, letterSpacing: '0.05em' }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <h2 style={{
          fontFamily: heritage, fontWeight: 700,
          fontSize: 'clamp(1.8rem, 4vw, 3.2rem)',
          letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1.1, marginBottom: 28,
        }}>
          Prêt à fusionner<br />
          <span style={{
            background: `linear-gradient(90deg, ${neonMagenta}, ${neonCyan})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>les époques ?</span>
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-block', padding: '14px 42px',
          background: `linear-gradient(135deg, ${neonMagenta}, ${neonCyan})`,
          color: dark, fontFamily: heritage, fontWeight: 700, fontSize: 14,
          letterSpacing: '0.1em', textTransform: 'uppercase', textDecoration: 'none',
          boxShadow: `0 0 30px ${neonMagenta}33, 0 0 60px ${neonCyan}22`,
          transition: 'all 0.3s',
        }}>Initier l'hybridation</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: 'relative', zIndex: 5, padding: '16px 0', borderTop: '1px solid #1a1a1d' }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: heritage, fontSize: 12, color: dimText, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Doctif — Direction Cyber-Heritage
          </span>
          <span style={{ fontFamily: cyber, fontSize: 11, color: '#444' }}>v13.0 — {new Date().getFullYear()}</span>
        </div>
      </footer>
      <div style={{
        height: 2,
        background: `linear-gradient(90deg, ${neonCyan}, ${neonMagenta}, ${neonCyan})`,
        boxShadow: `0 0 15px ${neonCyan}33`,
      }} />
    </div>
  );
}
